import {AfterViewInit,Component,createNgModule,Injector,NgModuleRef,OnDestroy,ViewChild,ViewContainerRef} from '@angular/core';
import {HttpBackend,HttpClient} from '@angular/common/http';
import {LocalStorageService} from 'angular-web-storage';
import {defer,throwError} from 'rxjs';
import {map,catchError,finalize} from 'rxjs/operators';
import {CalendarService} from './calendar.service';
import {McpProviderConnectionService} from './mcp-provider-connection.service';
import {environment} from '../../environments/environment';

/** Hosts the existing iCloud form with a scoped, memory-only connector session.
 * No normal login storage or global HTTP interceptors are changed.
 */
@Component({selector:'app-mcp-icloud-form',standalone:true,template:'<ng-container #form></ng-container>'})
export class McpIcloudFormComponent implements AfterViewInit,OnDestroy{
 @ViewChild('form',{read:ViewContainerRef,static:true}) form!:ViewContainerRef;
 private module?:NgModuleRef<any>;
 private destroyed=false;
 private submitted=false;
 constructor(private injector:Injector,private backend:HttpBackend,private connection:McpProviderConnectionService){}
 async ngAfterViewInit(){
  const session=this.connection.credentialSession;if(!session)return;
  try{
   const [{WidgetModule},{IcloudCalendarComponent}]=await Promise.all([import('../widget/widget.module'),import('../templates/icloud-calendar/icloud-calendar.component')]);
   if(this.destroyed)return;
   // HttpBackend bypasses the global login interceptor intentionally. This form
   // sends its memory-only handoff token to one fixed Mango endpoint; it must not
   // inherit an unrelated browser login. Keep the scoped provider below local.
   const client=new HttpClient(this.backend);
   const adapter={updateIcloudCredentials:(payload:any)=>defer(()=>{
    if(this.submitted||!session.authToken)return throwError(()=>({error:{message:'This connection has already been submitted. Return to chat to check it.'}}));
    this.submitted=true;
    // Use only the target authenticated by the MCP handoff, never browser storage.
    const credential=payload.icloudCalendarAccountModel||{};
    const source=credential.accountType==='icalAccount'?{accountType:'icalAccount',userName:credential.userName,password:credential.password}:{accountType:'icalUrl',icalUrl:credential.icalUrl};
    return client.post(environment.baseURL+'calendar/icloudcredentials',{userMirrorModel:{mirror:{id:session.mirrorId}},widgetSettingId:session.widgetId,isReconnectRequest:false,icloudCalendarAccountModel:source},{headers:{authToken:session.authToken,source:'webApp','Accept-Language':'en'}}).pipe(map((res:any)=>{
     const account=res?.object?.iCalCalendarAccountModel;
     if(res?.status!=='SUCCESS'||!Number.isSafeInteger(account?.id))throw {error:{message:'The connection was not confirmed. Return to chat to check calendars before retrying.'}};
     // The existing form expects this response shape; discard passwords/tokens.
     return {object:{iCalCalendarAccountModel:{id:account.id,accountType:account.accountType,userName:account.userName},selectedCalendar:[]}};
    }),catchError(()=>throwError(()=>({error:{message:'Mango could not confirm the connection. Check the details, or return to chat to check existing calendars before retrying.'}}))),finalize(()=>{this.submitted=false;}));
   })};
   // Reuse the production form component through a child injector rather than
   // duplicate its inputs/validation or modify global providers for the whole app.
   const scoped=Injector.create({parent:this.injector,providers:[
    {provide:LocalStorageService,useValue:{get:(key:string)=>key==='activeMirrorDetails'?{mirror:{id:session.mirrorId}}:null}},
    {provide:CalendarService,useValue:adapter},
   ]});
   this.module=createNgModule(WidgetModule,scoped);
   const component=this.form.createComponent(IcloudCalendarComponent,{injector:scoped,ngModuleRef:this.module});
   component.setInput('widgetSettingId',session.widgetId);
   component.setInput('icloudAlertModal',{hide:()=>{if(!this.submitted)void this.connection.finishCredentials();}});
   component.instance.icloudCalendarEventEmiter.subscribe((event:any)=>void this.connection.finishCredentials(event.icloudCredentials.id));
  }catch{this.connection.credentialSession=null;session.authToken='';this.connection.finished=true;this.connection.status='The iCloud form could not open. Return to chat and request a fresh connection.';}
 }
 ngOnDestroy(){this.destroyed=true;this.form.clear();this.module?.destroy();}
}
